import {
  useState,
  useEffect,
  useRef
} from 'react';
import { isNativeApp } from '../../utils/nativeApp';
import {
  registerNativeDeviceSession,
  revokeAllStudentDeviceSessions,
  getNativeDeviceToken,
  restoreNativeDeviceSession
} from '../../utils/nativeDeviceAuth';
import {
  authenticateSavedDeviceLogin,
  getSavedDeviceAuthAvailability
} from '../../utils/nativeDeviceUnlock';

async function createStudentWebSession(
  studentId,
  password
) {
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/student_web_password_login`,
      {
        method: 'POST',
        headers: {
          apikey:
            SUPABASE_KEY,
          Authorization:
            'Bearer ' +
            SUPABASE_KEY,
          'Content-Type':
            'application/json'
        },
        body:
          JSON.stringify({
            p_student_id:
              studentId,
            p_password:
              password
          })
      }
    );

  const result =
    await response.json();

  if (
    !response.ok ||
    !result?.ok ||
    !result?.token
  ) {
    throw new Error(
      result?.message ||
      'Could not create the student web session.'
    );
  }

  return result;
}

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

async function lookupStudent(studentCode) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students?student_code=eq.${encodeURIComponent(studentCode.trim())}&select=id,student_code,english_name,chinese_name,email,password_set,is_master&active=eq.true`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  const data = await response.json();
  return data[0] || null;
}

async function verifyPassword(studentId, password) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/verify_student_password`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        p_student_id: studentId,
        p_password: password
      })
    }
  );

  return response.json();
}

async function setStudentPassword(studentId, password) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/set_student_password`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        p_student_id: studentId,
        p_password: password
      })
    }
  );
}

async function hashCode(code) {
  const encoder = new TextEncoder();
  const data = encoder.encode(code);
  const hashBuffer = await crypto.subtle.digest(
    'SHA-256',
    data
  );
  const hashArray = Array.from(
    new Uint8Array(hashBuffer)
  );

  return hashArray
    .map(b =>
      b.toString(16).padStart(2, '0')
    )
    .join('');
}

async function storeOtp(email, code) {
  const hash = await hashCode(code);
  const expiresAt = new Date(
    Date.now() + 10 * 60 * 1000
  ).toISOString();

  await fetch(
    `${SUPABASE_URL}/rest/v1/otp_codes?email=eq.${encodeURIComponent(email)}&used=eq.false`,
    {
      method: 'DELETE',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  await fetch(
    `${SUPABASE_URL}/rest/v1/otp_codes`,
    {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        code_hash: hash,
        expires_at: expiresAt
      })
    }
  );
}

async function sendOtpEmail(email, code) {
  await fetch(
    API_URL,
    {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        action: 'sendOtp',
        email,
        code
      })
    }
  );
}

async function verifyOtp(email, code) {
  const hash = await hashCode(code);

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/otp_codes?email=eq.${encodeURIComponent(email)}&code_hash=eq.${hash}&used=eq.false&select=id,expires_at`,
    {
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY,
        Accept: 'application/vnd.pgrst.object+json'
      }
    }
  );

  const record = await response.json();

  if (!record || !record.id) {
    throw new Error(
      'Invalid or expired code.'
    );
  }

  if (
    new Date(record.expires_at) <
    new Date()
  ) {
    throw new Error(
      'Code has expired.'
    );
  }

  await fetch(
    `${SUPABASE_URL}/rest/v1/otp_codes?id=eq.${record.id}`,
    {
      method: 'PATCH',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        used: true
      })
    }
  );

  return true;
}

function saveStudentSession(
  student,
  webSessionToken = null
) {
  const session = {
    studentId: student.id,
    studentCode: student.student_code,
    englishName: student.english_name,
    chineseName: student.chinese_name,
    email: student.email,
    isMaster: student.is_master,
    testingMode: student.is_master === true,
    webSessionToken:
  webSessionToken || null,
    expiresAt:
      Date.now() +
      (30 * 24 * 60 * 60 * 1000)
  };

  localStorage.setItem(
    'studentSession',
    JSON.stringify(session)
  );

  return session;
}

export default function StudentLogin({
  onLogin,
  initialIdentifier = ''
}) {
  const [stage, setStage] =
    useState('id');

  // id | otp_first | set_password | password |
  // otp_forgot | reset_password

  const [studentCode, setStudentCode] =
    useState(
      String(
        initialIdentifier || ''
      ).trim()
    );

  const [student, setStudent] =
    useState(null);

  const [password, setPassword] =
    useState('');

  const [
    confirmPassword,
    setConfirmPassword
  ] = useState('');

  const [otpCode, setOtpCode] =
    useState('');

  const [loading, setLoading] =
    useState(false);

  const [
    passkeyLoading,
    setPasskeyLoading
  ] = useState(false);

  const [
    hasSavedDeviceLogin,
    setHasSavedDeviceLogin
  ] = useState(false);

  const [
    savedDeviceAuthAvailable,
    setSavedDeviceAuthAvailable
  ] = useState(false);

  const [error, setError] =
    useState('');

  const initialLookupStartedRef =
    useRef(false);

  useEffect(() => {
    if (!isNativeApp()) {
      return undefined;
    }

    let cancelled = false;

    async function checkSavedLogin() {
      try {
        const [
          token,
          authAvailability
        ] = await Promise.all([
          getNativeDeviceToken(),
          getSavedDeviceAuthAvailability()
        ]);

        if (!cancelled) {
          const hasToken =
            Boolean(token);

          setHasSavedDeviceLogin(
            hasToken
          );

          setSavedDeviceAuthAvailable(
            hasToken &&
            authAvailability.canAuthenticate
          );
        }
      } catch (err) {
        console.error(
          'Checking saved device login failed:',
          err
        );

        if (!cancelled) {
          setHasSavedDeviceLogin(false);
          setSavedDeviceAuthAvailable(false);
        }
      }
    }

    checkSavedLogin();

    return () => {
      cancelled = true;
    };
  }, []);

  const studentName =
    student
      ? (
          student.english_name ||
          student.chinese_name ||
          student.student_code
        )
      : '';

  async function finishLogin(
    studentForSession,
    passwordForDevice,
    {
      revokeOtherDevices = false
    } = {}
  ) {
    if (isNativeApp()) {
      const nativeSession =
        await registerNativeDeviceSession(
          studentForSession,
          passwordForDevice,
          {
            revokeOtherDevices
          }
        );

      onLogin(nativeSession);
      return;
    }

    if (revokeOtherDevices) {
      await revokeAllStudentDeviceSessions(
        studentForSession.id,
        passwordForDevice
      );
    }

    const webLogin =
  await createStudentWebSession(
    studentForSession.id,
    passwordForDevice
  );

const webSession =
  saveStudentSession(
    studentForSession,
    webLogin.token
  );

onLogin(webSession);
  }

  async function handlePasskeyLogin() {
    setError('');
    setPasskeyLoading(true);

    try {
      const unlocked =
        await authenticateSavedDeviceLogin();

      if (!unlocked) {
        return;
      }

      const nativeSession =
        await restoreNativeDeviceSession();

      if (!nativeSession) {
        setHasSavedDeviceLogin(false);

        throw new Error(
          'Saved passkey sign-in is no longer available. Please sign in with your student ID and password.'
        );
      }

      onLogin(nativeSession);
    } catch (err) {
      setError(
        err?.message ||
        'Passkey sign-in failed. Please try again or sign in with your student ID and password.'
      );
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function continueWithStudentCode(
    value
  ) {
    const cleanCode =
      String(value || '').trim();

    if (!cleanCode) {
      return;
    }

    setStudentCode(cleanCode);
    setError('');
    setLoading(true);

    try {
      const found =
        await lookupStudent(
          cleanCode
        );

      if (!found) {
        throw new Error(
          'Student ID not found. Please check and try again.'
        );
      }

      if (!found.email) {
        throw new Error(
          'No email address on file. Please contact your instructor.'
        );
      }

      setStudent(found);

      if (!found.password_set) {
        const code =
          String(
            Math.floor(
              100000 +
              Math.random() * 900000
            )
          );

        await storeOtp(
          found.email,
          code
        );

        await sendOtpEmail(
          found.email,
          code
        );

        setStage('otp_first');
      } else {
        setStage('password');
      }
    } catch (err) {
      setError(
        err?.message ||
        'Could not continue.'
      );
    } finally {
      setLoading(false);
    }
  }


  async function handleIdSubmit(e) {
    e.preventDefault();

    await continueWithStudentCode(
      studentCode
    );
  }


  useEffect(() => {
    const clean =
      String(
        initialIdentifier || ''
      ).trim();

    if (
      !clean ||
      initialLookupStartedRef.current
    ) {
      return;
    }

    initialLookupStartedRef.current =
      true;

    continueWithStudentCode(
      clean
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialIdentifier]);

  async function handleOtpFirst(e) {
    e.preventDefault();

    setError('');
    setLoading(true);

    try {
      await verifyOtp(
        student.email,
        otpCode
      );

      setOtpCode('');
      setStage('set_password');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSetPassword(e) {
    e.preventDefault();

    setError('');

    if (password.length < 8) {
      return setError(
        'Password must be at least 8 characters.'
      );
    }

    if (
      password !== confirmPassword
    ) {
      return setError(
        'Passwords do not match.'
      );
    }

    setLoading(true);

    try {
      await setStudentPassword(
        student.id,
        password
      );

      const updated = {
        ...student,
        password_set: true
      };

      await finishLogin(
        updated,
        password
      );
    } catch (err) {
      setError(
        'Failed to set password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordSubmit(
    e
  ) {
    e.preventDefault();

    setError('');
    setLoading(true);

    try {
      const ok =
        await verifyPassword(
          student.id,
          password
        );

      if (!ok) {
        throw new Error(
          'Incorrect password. Please try again.'
        );
      }

      await finishLogin(
        student,
        password
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!student?.email) {
      setError(
        'No email address on file. Please contact your instructor.'
      );
      return;
    }

    setError('');
    setLoading(true);

    try {
      const code =
        String(
          Math.floor(
            100000 +
            Math.random() * 900000
          )
        );

      await storeOtp(
        student.email,
        code
      );

      await sendOtpEmail(
        student.email,
        code
      );

      setStage('otp_forgot');
    } catch (err) {
      setError(
        'Failed to send code. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpForgot(e) {
    e.preventDefault();

    setError('');
    setLoading(true);

    try {
      await verifyOtp(
        student.email,
        otpCode
      );

      setOtpCode('');
      setPassword('');
      setConfirmPassword('');
      setStage('reset_password');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(
    e
  ) {
    e.preventDefault();

    setError('');

    if (password.length < 8) {
      return setError(
        'Password must be at least 8 characters.'
      );
    }

    if (
      password !== confirmPassword
    ) {
      return setError(
        'Passwords do not match.'
      );
    }

    setLoading(true);

    try {
      await setStudentPassword(
        student.id,
        password
      );

      await finishLogin(
        student,
        password,
        {
          revokeOtherDevices: true
        }
      );
    } catch (err) {
      setError(
        'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h1 style={styles.title}>
          Student Portal
        </h1>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {stage === 'id' && (
          <form
            onSubmit={handleIdSubmit}
          >
            <p style={styles.sub}>
              Enter your student ID to continue.
            </p>

            <label style={styles.label}>
              Student ID
            </label>

            <input
              style={styles.input}
              type="text"
              value={studentCode}
              onChange={e =>
                setStudentCode(
                  e.target.value
                )
              }
              placeholder="e.g. S15123456"
              autoFocus
              required
            />

            <button
              style={styles.btn}
              disabled={
                loading ||
                passkeyLoading
              }
            >
              {loading
                ? 'Looking up...'
                : 'Continue'}
            </button>

            {hasSavedDeviceLogin &&
              savedDeviceAuthAvailable && (
                <>
                  <div
                    style={
                      styles.divider
                    }
                  >
                    or
                  </div>

                  <button
                    type="button"
                    style={
                      styles.passkeyBtn
                    }
                    onClick={
                      handlePasskeyLogin
                    }
                    disabled={
                      loading ||
                      passkeyLoading
                    }
                  >
                    {passkeyLoading
                      ? 'Signing in...'
                      : 'Sign in with Passkey'}
                  </button>
                </>
              )}
          </form>
        )}

        {stage === 'otp_first' && (
          <form
            onSubmit={handleOtpFirst}
          >
            <p style={styles.sub}>
              Welcome,{' '}
              <strong>
                {studentName}
              </strong>
              . A verification code has
              been sent to{' '}
              <strong>
                {student?.email}
              </strong>
              . Please enter it below
              to set up your account.
            </p>

            <label style={styles.label}>
              Verification code
            </label>

            <input
              style={styles.input}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={e =>
                setOtpCode(
                  e.target.value
                )
              }
              placeholder="000000"
              autoFocus
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Verifying...'
                : 'Verify'}
            </button>

            <button
              type="button"
              style={
                styles.secondaryBtn
              }
              onClick={() => {
                setStage('id');
                setStudent(null);
                setOtpCode('');
              }}
            >
              ← Back
            </button>
          </form>
        )}

        {stage === 'set_password' && (
          <form
            onSubmit={
              handleSetPassword
            }
          >
            <p style={styles.sub}>
              Identity verified.
              Please set a password
              for your account.
            </p>

            <label style={styles.label}>
              New password
            </label>

            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e =>
                setPassword(
                  e.target.value
                )
              }
              placeholder="At least 8 characters"
              autoFocus
              required
            />

            <label style={styles.label}>
              Confirm password
            </label>

            <input
              style={styles.input}
              type="password"
              value={
                confirmPassword
              }
              onChange={e =>
                setConfirmPassword(
                  e.target.value
                )
              }
              placeholder="Repeat password"
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Setting password...'
                : 'Set Password & Sign In'}
            </button>
          </form>
        )}

        {stage === 'password' && (
          <form
            onSubmit={
              handlePasswordSubmit
            }
          >
            <p style={styles.sub}>
              Welcome back,{' '}
              <strong>
                {studentName}
              </strong>
              .
            </p>

            <label style={styles.label}>
              Password
            </label>

            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e =>
                setPassword(
                  e.target.value
                )
              }
              autoFocus
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Signing in...'
                : 'Sign In'}
            </button>

            <button
              type="button"
              style={
                styles.secondaryBtn
              }
              onClick={
                handleForgotPassword
              }
              disabled={loading}
            >
              Forgot password?
            </button>

            <button
              type="button"
              style={
                styles.secondaryBtn
              }
              onClick={() => {
                setStage('id');
                setStudent(null);
                setPassword('');
              }}
            >
              ← Back
            </button>
          </form>
        )}

        {stage === 'otp_forgot' && (
          <form
            onSubmit={
              handleOtpForgot
            }
          >
            <p style={styles.sub}>
              A reset code has been
              sent to{' '}
              <strong>
                {student?.email}
              </strong>
              .
            </p>

            <label style={styles.label}>
              Verification code
            </label>

            <input
              style={styles.input}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={e =>
                setOtpCode(
                  e.target.value
                )
              }
              placeholder="000000"
              autoFocus
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Verifying...'
                : 'Verify'}
            </button>

            <button
              type="button"
              style={
                styles.secondaryBtn
              }
              onClick={() => {
                setStage(
                  'password'
                );
                setOtpCode('');
              }}
            >
              ← Back
            </button>
          </form>
        )}

        {stage ===
          'reset_password' && (
          <form
            onSubmit={
              handleResetPassword
            }
          >
            <p style={styles.sub}>
              Enter a new password
              for your account.
            </p>

            <label style={styles.label}>
              New password
            </label>

            <input
              style={styles.input}
              type="password"
              value={password}
              onChange={e =>
                setPassword(
                  e.target.value
                )
              }
              placeholder="At least 8 characters"
              autoFocus
              required
            />

            <label style={styles.label}>
              Confirm password
            </label>

            <input
              style={styles.input}
              type="password"
              value={
                confirmPassword
              }
              onChange={e =>
                setConfirmPassword(
                  e.target.value
                )
              }
              placeholder="Repeat password"
              required
            />

            <button
              style={styles.btn}
              disabled={loading}
            >
              {loading
                ? 'Resetting...'
                : 'Reset Password & Sign In'}
            </button>
          </form>
        )}

        <div style={styles.privacy}>
          <a
  href="/privacy.html"
  style={
    styles.privacyLink
  }
>
  Privacy Policy
</a>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '100vh',
    background: '#102a43',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  },

  card: {
    background: 'white',
    borderRadius: '6px',
    padding: '42px 40px',
    border:
      '1px solid rgba(255,255,255,0.18)',
    width: '100%',
    maxWidth: '430px',
    boxShadow: 'none'
  },

  title: {
    margin: '0 0 8px',
    fontSize: '28px',
    color: '#243b53'
  },

  sub: {
    margin: '0 0 20px',
    color: '#444',
    fontSize: '14px',
    lineHeight: '1.5'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '5px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#243b53'
  },

  input: {
    width: '100%',
    border:
      '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '10px 12px',
    fontSize: '16px',
    marginBottom: '14px',
    boxSizing: 'border-box'
  },

  btn: {
    width: '100%',
    background: '#2f6f9f',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    padding: '11px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '10px'
  },

  secondaryBtn: {
    width: '100%',
    background: 'transparent',
    color: '#627d98',
    border:
      '1px solid #d9e2ec',
    borderRadius: '5px',
    padding: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    marginBottom: '8px'
  },

  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#829ab1',
    fontSize: '12px',
    margin: '2px 0 10px'
  },

  passkeyBtn: {
    width: '100%',
    background: '#f7f9fb',
    color: '#243b53',
    border:
      '1px solid #bcccdc',
    borderRadius: '5px',
    padding: '11px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '8px'
  },

  privacy: {
    marginTop: '22px',
    textAlign: 'center',
    fontSize: '12px'
  },

  privacyLink: {
    color: '#627d98',
    textDecoration: 'underline'
  }
};